// Deal status of a unit (docs/decisions/inventory-columns.md): the backend
// sets `DealStatus` and `DealStatusDate`; this file only names and orders
// them. Precedence: Sold > Pre-sold > Sale pending > On hold > Quoted >
// Available (Pre-sold is the more specific committed state: marked sold
// before the unit is received, even when Salesforce says Sold Pending).
// On hold (decision 6, changed 16 Oct 2026) is a unit in stock that
// Salesforce holds; `DealStatusReason` carries Equip's alert text, if any.
// Unknown values are not shown.

// Sort key of the Deal status column (sorts by precedence, not by text).
export const DEAL_STATUS_SORT_KEY = 'DealStatus';

const STATUSES = Object.freeze({
    sold: { label: 'Sold', tone: 'sold', rank: 6 },
    pre_sold: { label: 'Pre-sold', tone: 'pending', rank: 5 },
    sale_pending: { label: 'Sale pending', tone: 'pending', rank: 4 },
    on_hold: { label: 'On hold', tone: 'hold', rank: 3 },
    quoted: { label: 'Quoted', tone: 'quoted', rank: 2 },
    available: { label: 'Available', tone: 'available', rank: 1 },
});

// The committed statuses (never in the regular inventory's KPIs).
export const COMMITTED_STATUSES = Object.freeze(['sold', 'pre_sold', 'sale_pending']);

// Order of the control card's breakdown (as users read the pipeline).
const BREAKDOWN_ORDER = Object.freeze(['sale_pending', 'pre_sold', 'sold']);

/** "Sale pending 353 · Pre-sold 357 · Sold 22" for committed rows; zero entries left out. */
export const committedBreakdown = (units) => {
    const counts = {};
    (units || []).forEach((u) => { counts[u?.DealStatus] = (counts[u?.DealStatus] || 0) + 1; });
    return BREAKDOWN_ORDER.filter((st) => counts[st])
        .map((st) => `${STATUSES[st].label} ${counts[st].toLocaleString('en-US')}`)
        .join(' · ');
};

const ISO_DATE = /^(\d{4})-(\d{2})-(\d{2})/;

/** "Oct 3, 2026" for an ISO date (read as a calendar date, no time zone shift), else null. */
export const formatDealDate = (iso) => {
    const m = typeof iso === 'string' ? ISO_DATE.exec(iso) : null;
    if (!m) return null;
    const date = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])));
    if (Number.isNaN(date.getTime())) return null;
    return date.toLocaleDateString('en-US', {
        month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC',
    });
};

/** "Sep 22" for a date in `today`'s year, else "Nov 17, 2025" (QA E6). */
export const shortDealDate = (full, today = new Date()) => (
    full && full.endsWith(`, ${today.getFullYear()}`) ? full.replace(/, \d{4}$/, '') : full
);

// What `DealStatusDate` means per status (backend): Sold carries the invoice
// date; Sale pending and Pre-sold the date the unit was marked sold.
const DATE_VERB = Object.freeze({ sold: 'invoiced', sale_pending: 'marked sold', pre_sold: 'marked sold' });

// The hold reason as the backend sends it (already cleaned), or null.
const holdReason = (unit) => {
    const text = typeof unit?.DealStatusReason === 'string' ? unit.DealStatusReason.trim() : '';
    return text || null;
};

const tipFor = (status, date, reason) => {
    switch (status) {
    case 'quoted': return 'Active quote. Not a commitment.';
    case 'on_hold': return reason ? `On hold: ${reason}` : 'On hold in Salesforce. Reason not recorded.';
    case 'sale_pending': return date ? `Marked sold on ${date}, not yet invoiced.` : 'Marked sold, not yet invoiced.';
    case 'pre_sold': return date ? `Sold before the unit was received. Marked sold on ${date}.` : 'Sold before the unit was received.';
    case 'sold': return date ? `Invoiced on ${date}, awaiting close-out.` : 'Invoiced, awaiting close-out.';
    default: return null;
    }
};

/**
 * { status, label, tone, tip, date, shortDate, dateText, reason } for the
 * unit's chip, or null (no status to show). `date` is the full date ("Nov
 * 17, 2025"), `shortDate` drops this year's year, `dateText` names what the
 * date is ("marked sold Sep 22", "invoiced Dec 3, 2025"); the last two only
 * for a committed status. `reason` is the hold reason (On hold only).
 */
export const dealStatusView = (unit, today = new Date()) => {
    const status = unit?.DealStatus;
    const def = Object.prototype.hasOwnProperty.call(STATUSES, status) ? STATUSES[status] : null;
    if (!def) return null;
    const date = formatDealDate(unit.DealStatusDate);
    const verb = DATE_VERB[status] || null;
    const shortDate = verb && date ? shortDealDate(date, today) : null;
    const reason = status === 'on_hold' ? holdReason(unit) : null;
    return {
        status,
        label: def.label,
        tone: def.tone,
        tip: tipFor(status, date, reason),
        date,
        shortDate,
        dateText: shortDate ? `${verb} ${shortDate}` : null,
        reason,
    };
};

/** Precedence rank (6 Sold … 3 On hold … 1 Available), 0 for no shown status. */
export const dealStatusRank = (unit) => {
    const status = unit?.DealStatus;
    return Object.prototype.hasOwnProperty.call(STATUSES, status) ? STATUSES[status].rank : 0;
};
